import ApolloClient from 'apollo-client'
import { useHistory } from 'react-router-dom'
import { routes } from '@sketch/constants'
import { isViewingCurrentProject } from 'modules/projects/utils'
import { ErrorHandler } from '@sketch/tracing'
import { useEventDispatch } from '@sketch/utils'
import {
  GetProjectsDocument,
  GetProjectsQuery,
  GetProjectsQueryVariables,
  useProjectAccessChangedSubscription,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { getToast } from 'modules/projects/toasts'

declare module '@sketch/utils' {
  export interface EventsMap {
    projectAccessRevoked: {
      workspaceIdentifier: string
      projectIdentifier: string
      projectName: string
    }
  }
}

export const useProjectMembershipSubscriptions = () => {
  const history = useHistory()
  const { showToast } = useToast()
  const dispatchSharesRefresh = useEventDispatch('workspaceShareRefresh')
  const dispatchProjectAccessRevoked = useEventDispatch('projectAccessRevoked')

  /**
   * Project Access Changed Mutation
   *
   * Mutation fired when project access changes
   * for the current logged user.
   * Projects' list will be refreshed to reflect
   * the latest projects that user has access to.
   */
  useProjectAccessChangedSubscription({
    onSubscriptionData({ client, subscriptionData: { data, error, loading } }) {
      const responseData = data?.projectAccessChanged

      if (!responseData || error || loading) {
        ErrorHandler.shouldNeverHappen(
          'Received "projectAccessChanged" subscription data should always be valid one'
        )
        return
      }

      const { workspaceIdentifier } = responseData

      refetchProjects(client, workspaceIdentifier)

      // Force the Shares view to refresh for All Documents to refresh, if visible
      // see src/modules/shares/components/WorkspaceSharesList.tsx
      dispatchSharesRefresh({
        workspaceIdentifier,
      })

      // User loses access to the project
      if (responseData.__typename === 'ProjectAccessRevokedEvent') {
        if (isViewingCurrentProject(window.location.pathname)) {
          history.push(
            routes.WORKSPACE_SHARES.create({ workspaceId: workspaceIdentifier })
          )

          showToast(
            getToast('PROJECT_ACCESS_REVOKED', responseData.project.name),
            'default'
          )
        }

        dispatchProjectAccessRevoked({
          workspaceIdentifier: responseData.workspaceIdentifier,
          projectIdentifier: responseData.project.identifier,
          projectName: responseData.project.name,
        })
      }
    },
  })
}

const refetchProjects = (client: ApolloClient<object>, workspaceId: string) => {
  client.query<GetProjectsQuery, GetProjectsQueryVariables>({
    query: GetProjectsDocument,
    variables: { workspaceId },
    fetchPolicy: 'network-only',
  })
}
